import { useState } from "react"

import { useTranslation } from "react-i18next"

import {
  Activity,
  ChevronDown,
  ChevronUp,
  Gauge,
  Pencil,
  Shield,
  ShieldCheck,
  Trash2,
} from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { Switch } from "@/components/ui/switch"

import { AdminTableToolbar } from "./admin-table-toolbar"
import { useAdminTableControls } from "../hooks/use-admin-table-controls"

/** Which registry list the rows came from; drives the chat-only affordances. */
export type ModelsTableKind = "chat" | "embedding" | "specialized"

export type ModelsTableRow = {
  id: string
  displayName: string
  description?: string
  deployment?: string
  enabled: boolean
  requiredModules?: string[]
  access_configured?: boolean
  effort_access_configured?: boolean
  model_type?: string
  /** Azure resource the deployment lives on; seeds the add dialog. */
  service_name?: string
  /** Specialized rows: image / realtime / video / audio. */
  type?: string
  /** Image rows: the Azure image family id behind the deployment. */
  model?: string
  /**
   * Fair-use plan allowlist (#3087 R2). `null` / absent = every plan, which is
   * why the badge is driven by a non-empty array and never by "is the key
   * there" — the backend removes the key rather than storing `[]`.
   */
  plans?: string[] | null
}

export function ModelsTable({
  models,
  onToggleStatus,
  onEditAccess,
  onEditEffortAccess,
  onEdit,
  onDelete,
  isUpdating,
  disableAll,
  kind = "chat",
  emptyDescription,
}: {
  models: ModelsTableRow[]
  onToggleStatus: (modelId: string, enabled: boolean) => void
  onEditAccess?: (modelId: string) => void
  onEditEffortAccess?: (modelId: string) => void
  /** Opens the Form + Raw edit dialog for this model. */
  onEdit?: (modelId: string) => void
  onDelete?: (model: { id: string; displayName: string }) => void
  isUpdating?: string
  disableAll?: boolean
  kind?: ModelsTableKind
  /** Per-tab empty-state copy; falls back to the generic "No models configured". */
  emptyDescription?: string
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  // Reasoning effort is a chat-model concept, so the per-model effort gate has
  // nothing to act on for embedding or specialized deployments.
  const showEffortAccess = kind === "chat"

  const controls = useAdminTableControls(models, {
    getName: (row) => row.displayName,
    getSearchText: (row) => [row.displayName, row.id, row.description, row.deployment].join(" "),
    isEnabled: (row) => row.enabled,
  })
  const displayModels = isExpanded ? controls.rows : controls.rows.slice(0, 5)
  const hasMoreModels = controls.count > 5

  if (models.length === 0) {
    return (
      <EmptyState
        icon={Activity}
        title={t("adminDashboard.models.emptyTitle", "No Models")}
        description={
          emptyDescription ?? t("adminDashboard.models.emptyDescription", "No models configured")
        }
      />
    )
  }

  return (
    <div className="space-y-3">
      <AdminTableToolbar bulkActions controls={controls} />
      <div className="text-muted-foreground hidden grid-cols-12 gap-4 px-4 py-2 text-sm font-medium lg:grid">
        <div className="col-span-3">{t("adminDashboard.models.name", "Model Name")}</div>
        <div className="col-span-3">{t("adminDashboard.models.description", "Description")}</div>
        <div className="col-span-2">{t("adminDashboard.models.deployment", "Deployment")}</div>
        <div className="col-span-2 text-right">{t("adminDashboard.models.actions", "Actions")}</div>
        <div className="col-span-2 text-right">{t("adminDashboard.models.status", "Status")}</div>
      </div>

      {displayModels.map((model) => (
        <div
          key={model.id}
          className="admin-responsive-grid-row bg-card hover:bg-accent/50 grid grid-cols-12 gap-4 rounded-lg border p-4 text-sm transition-colors"
        >
          <div className="col-span-3 flex flex-col">
            <span className="flex items-center gap-2">
              <span className="font-medium">{model.displayName}</span>
              {kind === "specialized" && model.type ? (
                <Badge className="font-normal" variant="secondary">
                  {model.type}
                </Badge>
              ) : null}
              {model.plans && model.plans.length > 0 ? (
                <Badge
                  className="font-normal"
                  variant="outline"
                  title={t("adminDashboard.models.plansBadgeTitle", {
                    defaultValue: "Only these fair-use plans may pick this model",
                  })}
                >
                  {model.plans.join(" · ")}
                </Badge>
              ) : null}
            </span>
            <span className="text-muted-foreground text-xs">{model.id}</span>
          </div>
          <div className="text-muted-foreground col-span-3 flex items-start gap-3 lg:block">
            <span className="text-foreground w-24 shrink-0 text-xs font-medium lg:hidden">
              Description
            </span>
            <span>{model.description || "—"}</span>
          </div>
          <div className="text-muted-foreground col-span-2 flex items-start gap-3 font-mono text-xs lg:block">
            <span className="text-foreground w-24 shrink-0 font-sans font-medium lg:hidden">
              Deployment
            </span>
            <span>{model.deployment || "—"}</span>
            {kind === "specialized" && model.type === "image" && model.model ? (
              <span className="text-muted-foreground/80 block">{model.model}</span>
            ) : null}
          </div>
          <div className="col-span-2 flex items-center justify-end gap-1">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Actions
            </span>
            {onEdit ? (
              <Button
                aria-label={`${t("adminDashboard.models.edit", { defaultValue: "Edit model" })} ${model.displayName}`}
                disabled={disableAll}
                size="icon"
                title={t("adminDashboard.models.edit", { defaultValue: "Edit model" })}
                type="button"
                variant="ghost"
                onClick={() => onEdit(model.id)}
              >
                <Pencil className="size-4" />
              </Button>
            ) : null}
            <Button
              aria-label={`${t("adminDashboard.access.access", { defaultValue: "Access" })} ${model.displayName}`}
              disabled={disableAll || !onEditAccess}
              size="icon"
              title={t("adminDashboard.access.access", { defaultValue: "Access" })}
              type="button"
              variant="outline"
              onClick={() => onEditAccess?.(model.id)}
            >
              {model.access_configured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
            </Button>
            {showEffortAccess ? (
              <Button
                aria-label={`${t("adminDashboard.models.effortAccess", { defaultValue: "Effort access" })} ${model.displayName}`}
                disabled={disableAll || !onEditEffortAccess}
                size="icon"
                title={t("adminDashboard.models.effortAccess", { defaultValue: "Effort access" })}
                type="button"
                variant="outline"
                onClick={() => onEditEffortAccess?.(model.id)}
              >
                <Gauge
                  className={model.effort_access_configured ? "text-success size-4" : "size-4"}
                />
              </Button>
            ) : null}
            {onDelete && (
              <Button
                aria-label={`${t("adminDashboard.models.delete", "Delete model")} ${model.displayName}`}
                disabled={disableAll}
                size="icon"
                title={t("adminDashboard.models.delete", "Delete model")}
                type="button"
                variant="outline"
                onClick={() => onDelete({ id: model.id, displayName: model.displayName })}
              >
                <Trash2 className="text-destructive size-4" />
              </Button>
            )}
          </div>
          <div className="col-span-2 flex items-center justify-end">
            <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
              Status
            </span>
            <Switch
              aria-label={`${model.displayName} status`}
              checked={model.enabled}
              disabled={disableAll || isUpdating === model.id}
              onCheckedChange={(checked) => onToggleStatus(model.id, checked)}
            />
            <span className="text-muted-foreground ml-2 text-xs">
              {model.enabled
                ? t("adminDashboard.models.enabled", "Enabled")
                : t("adminDashboard.models.disabled", "Disabled")}
            </span>
          </div>
        </div>
      ))}

      {hasMoreModels && (
        <div className="flex justify-center pt-2">
          <Button
            className="text-muted-foreground hover:text-foreground w-full justify-center text-sm"
            variant="ghost"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.models.showLess", "Show Less")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.models.showMore", "Show More", {
                  count: controls.count - 5,
                })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
